<template>
  <div class="training-follow-up">
    <p class="text-muted">{{ $t("TRAININGS.FOLLOW_UP_DESCRIPTION") }}</p>
    <div v-if="loading" class="text-center py-4">
      <img src="/img/loading.gif" style="height: 80px; width: 80px" />
    </div>
    <p v-else-if="followUp.length === 0" class="text-muted">
      {{ $t("TRAININGS.NO_FOLLOW_UP") }}
    </p>
    <template v-else>
      <div class="d-flex flex-wrap mb-3">
        <base-input
          v-model="query"
          class="mr-3 mb-2 training-follow-up-search"
          prepend-icon="fas fa-search"
          :placeholder="$t('TRAININGS.SEARCH_EMPLOYEE')"
        />
        <el-select v-model="status" class="mr-3 mb-2">
          <el-option value="" :label="$t('COMMON.ALL')" />
          <el-option
            value="to-finish"
            :label="$t('TRAININGS.FILTER_TO_FINISH')"
          />
          <el-option
            value="finished"
            :label="$t('TRAININGS.FILTER_FINISHED')"
          />
        </el-select>
        <el-select
          v-model="role"
          clearable
          class="mb-2"
          :placeholder="$t('COMMON.ROLE')"
        >
          <el-option
            v-for="name in roles"
            :key="name"
            :value="name"
            :label="name"
          />
        </el-select>
      </div>

      <el-table
        class="table-responsive align-items-center table-flush"
        header-row-class-name="thead-light"
        :data="filteredUsers"
      >
        <el-table-column :label="$t('TRAININGS.EMPLOYEE')" min-width="200px">
          <template v-slot="{ row }">
            <router-link :to="{ name: 'View User', params: { id: row.id } }">
              {{ `${row.firstname} ${row.lastname}` }}
            </router-link>
            <small class="d-block text-muted">{{ row.email }}</small>
            <el-tooltip
              v-if="row.video_exempt"
              :content="$t('TRAININGS.EXEMPT_TOOLTIP')"
              placement="top"
            >
              <span class="badge badge-info mt-1">
                {{ $t("TRAININGS.EXEMPT") }}
              </span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column :label="$t('COMMON.ROLE')" min-width="150px">
          <template v-slot="{ row }">
            {{ row.roles.join(", ") }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('TRAININGS.PROGRESS')" min-width="150px">
          <template v-slot="{ row }">
            <base-progress
              :type="isFinished(row) ? 'success' : 'primary'"
              :value="percent(row)"
              :height="6"
            />
            <small>
              {{
                $t("TRAININGS.MY_PATH_PROGRESS", {
                  completed: row.completed,
                  total: row.total,
                })
              }}
            </small>
          </template>
        </el-table-column>
        <el-table-column :label="$t('TRAININGS.FINISHED_ON')" min-width="110px">
          <template v-slot="{ row }">
            {{
              row.finished_at ? $timeZoneDateFormat(row.finished_at, "LL") : "-"
            }}
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('TRAININGS.LAST_REMINDER')"
          min-width="140px"
        >
          <template v-slot="{ row }">
            {{
              row.reminded_at
                ? $timeZoneDateFormat(row.reminded_at, "LLL")
                : "-"
            }}
          </template>
        </el-table-column>
        <el-table-column min-width="140px" align="right">
          <template v-slot="{ row }">
            <base-button
              v-if="!isFinished(row)"
              size="sm"
              type="warning"
              outline
              class="text-nowrap"
              :disabled="remindingId === row.id"
              @click="remind(row)"
            >
              <i class="fa fa-bell mr-1"></i>
              {{ $t("TRAININGS.REMIND") }}
            </base-button>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import swal from "sweetalert2";
import { Option, Select, Table, TableColumn } from "element-ui";

export default {
  name: "training-follow-up",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },

  data() {
    return {
      loading: true,
      remindingId: null,
      query: "",
      status: "to-finish",
      role: null,
    };
  },

  computed: {
    ...mapGetters({ followUp: "trainings/followUp" }),
    roles() {
      return [...new Set(this.followUp.flatMap((user) => user.roles))].sort(
        (a, b) => a.localeCompare(b, "fr")
      );
    },
    filteredUsers() {
      const query = this.$unaccent(this.query.trim().toLowerCase());
      return this.followUp.filter((user) => {
        if (this.status === "finished" && !this.isFinished(user)) {
          return false;
        }
        if (this.status === "to-finish" && this.isFinished(user)) {
          return false;
        }
        if (this.role && !user.roles.includes(this.role)) {
          return false;
        }
        if (!query) {
          return true;
        }
        return this.$unaccent(
          `${user.firstname} ${user.lastname} ${user.email}`.toLowerCase()
        ).includes(query);
      });
    },
  },

  async created() {
    try {
      await this.$store.dispatch("trainings/followUp");
    } catch (error) {
      this.$notify({
        type: "danger",
        message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
      });
    }
    this.loading = false;
  },

  methods: {
    isFinished(user) {
      return user.total > 0 && user.completed === user.total;
    },

    percent(user) {
      return user.total ? Math.round((user.completed / user.total) * 100) : 0;
    },

    async remind(user) {
      const name = `${user.firstname} ${user.lastname}`;
      const confirmation = await swal.fire({
        title: this.$t("TRAININGS.REMIND_CONFIRM", { name }),
        type: "question",
        buttonsStyling: false,
        showCancelButton: true,
        confirmButtonText: this.$t("TRAININGS.REMIND"),
        cancelButtonText: this.$t("COMMON.CANCEL"),
        confirmButtonClass: "btn btn-primary",
        cancelButtonClass: "btn btn-warning",
      });
      if (!confirmation.value) {
        return;
      }

      this.remindingId = user.id;
      try {
        await this.$store.dispatch("trainings/remind", user.id);
        this.$notify({
          type: "success",
          message: this.$t("TRAININGS.REMINDED", { name }),
        });
      } catch (error) {
        this.$showRequestError(error);
      }
      this.remindingId = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.training-follow-up-search {
  min-width: 260px;
}
// The theme breaks table cells mid-word (« comptoi-r »).
.training-follow-up ::v-deep .el-table .cell {
  word-break: normal;
}
</style>
